import { useContext } from 'react';
import { I18nContext } from './I18nProvider';
import { DEFAULT_LOCALE, localeDescriptor } from './config';
import { createFormatters } from './format';
import { createTranslator } from './translator';

// ─────────────────────────────────────────────────────────────────────────────
// The only way a component reads a string.
//
//   const { t, formatDate, isRTL } = useTranslation();
//   <button>{t('common.actions.save')}</button>
//
// Keys are always fully qualified — the first segment names the file the string
// lives in (see translator.js). There is no scoped/`useTranslation('visit')`
// form on purpose: one rule means a key you see in a screen is a key you can
// grep for, and a string cannot be reached by two different names.
// ─────────────────────────────────────────────────────────────────────────────

// Rendered outside a provider — a component under test, or an error boundary
// that caught the provider itself. It reports the default locale and translates
// to the humanised key rather than throwing, because "no translations" must
// never be the reason a clinical screen fails to render.
let orphan = null;
function orphanValue() {
  if (!orphan) {
    const descriptor = localeDescriptor(DEFAULT_LOCALE);
    const formatters = createFormatters(DEFAULT_LOCALE);
    const { t, tList, has } = createTranslator({
      locale: DEFAULT_LOCALE,
      catalog: {},
      fallbackCatalog: {},
      joinList: formatters.formatList,
      formatNumber: formatters.formatNumber,
    });
    orphan = {
      locale: DEFAULT_LOCALE,
      dir: descriptor.dir,
      isRTL: descriptor.dir === 'rtl',
      readsPersianData: descriptor.readsPersianData === true,
      locales: {},
      t, tList, has,
      ...formatters,
      setLocale: () => {},
      preloadLocale: () => {},
      ready: false,
    };
  }
  return orphan;
}

export function useTranslation() {
  return useContext(I18nContext) ?? orphanValue();
}

export default useTranslation;
